import type { FC } from 'react';
import React, { memo } from 'react';
import styled from 'styled-components/native';

import { PathComp, SvgComp } from '../Svg';

import type { IconType } from '~types/views-types';

type IconProps = {
  cssColor: string;
  cssSize: number;
  icon: IconType;
  hoverState?: boolean;
};

const StyledIcon = styled(SvgComp)<StyledIconProps>`
  flex: 0 0 auto;

  ${({ hoverState }) =>
    hoverState &&
    `
    transform: scale(1.1);
  `}

  height: ${({ cssSize }) => `${cssSize}px`};
  width: ${({ cssSize }) => `${cssSize}px`};
`;

type StyledIconProps = {
  cssColor: string;
  cssSize: number;
  hoverState?: boolean;
};

const Icon: FC<IconProps> = memo(({ cssColor, cssSize, icon, hoverState }) => {
  const { viewBox, path } = IconData[icon];

  let pathOrPaths;

  if (typeof path === 'string') {
    pathOrPaths = <PathComp d={path} />;
  } else {
    pathOrPaths = path.map((p, i) => <PathComp key={i} d={p} />);
  }

  return (
    <StyledIcon
      hoverState={hoverState}
      fill={cssColor}
      height={cssSize}
      width={cssSize}
      cssSize={cssSize}
      viewBox={viewBox}
    >
      {pathOrPaths}
    </StyledIcon>
  );
});

export const IconData: Record<
  string,
  { path: string[] | string; viewBox: string }
> = {
  AccountCircle: {
    viewBox: '0 0 48 48',
    path: 'M24 4C12.95 4 4 12.95 4 24s8.95 20 20 20 20-8.95 20-20S35.05 4 24 4zm0 6c3.31 0 6 2.69 6 6 0 3.32-2.69 6-6 6s-6-2.68-6-6c0-3.31 2.69-6 6-6zm0 28.4c-5.01 0-9.41-2.56-12-6.44.05-3.97 8.01-6.16 12-6.16s11.94 2.19 12 6.16c-2.59 3.88-6.99 6.44-12 6.44z',
  },
  ArrowRight: {
    viewBox: '0 0 10 16',
    path: 'M1 15.071L8.07107 7.99998L1 0.92891',
  },
  ArrowDown: {
    viewBox: '0 0 16 10',
    path: 'M0.928955 0.998047L8.00002 8.06911L15.0711 0.998047',
  },
  ArrowDropDown: {
    viewBox: '0 0 36 36',
    path: 'M10.5 15l7.5 7.5 7.5-7.5z',
  },
  ArrowDropUp: {
    viewBox: '0 0 36 36',
    path: 'M10.5 21l7.5-7.5 7.5 7.5z',
  },
  Assignment: {
    viewBox: '0 0 48 48',
    path: 'M38 6h-8.37c-.82-2.32-3.02-4-5.63-4s-4.81 1.68-5.63 4H10c-2.21 0-4 1.79-4 4v28c0 2.21 1.79 4 4 4h28c2.21 0 4-1.79 4-4V10c0-2.21-1.79-4-4-4zM24 6c1.1 0 2 .89 2 2s-.9 2-2 2-2-.89-2-2 .9-2 2-2zm4 28H14v-4h14v4zm6-8H14v-4h20v4zm0-8H14v-4h20v4z',
  },
  BackToMag: {
    viewBox: '0 0 26 26',
    path: [
      'M 13,3 C 8.03,3 4,7.03 4,12 H 1 L 4.89,15.89 4.96,16.03 9,12 H 6 c 0,-3.87 3.13,-7 7,-7 3.87,0 7,3.13 7,7 0,3.87 -3.13,7 -7,7 -1.93,0 -3.68,-0.79 -4.94,-2.06 L 6.64,18.36 C 8.27,19.99 10.51,21 13,21 c 4.97,0 9,-4.03 9,-9 0,-4.97 -4.03,-9 -9,-9 z',
      'M 17.324284,7.7673109 A 4.1171089,4.1171089 0 0 0 12.929551,8.6873108 3.987157,3.987157 0 0 0 8.42554,7.8470539 0.44301745,0.44301745 0 0 0 8.1685899,8.2546305 v 7.3393225 a 0.40757606,0.40757606 0 0 0 0.5493417,0.383948 3.987157,3.987157 0 0 1 3.6135464,0.443018 1.0883462,1.0883462 0 0 0 0.614317,0.18902 1.0780091,1.0780091 0 0 0 0.601027,-0.181637 4.1156321,4.1156321 0 0 1 3.54414,-0.50504 0.3987157,0.3987157 0 0 0 0.121091,0.0192 0.4105295,0.4105295 0 0 0 0.407576,-0.410528 V 8.1837472 A 0.44301745,0.44301745 0 0 0 17.324284,7.7673109 Z M 12.30342,9.8701673 V 15.047564 A 5.1685369,5.1685369 0 0 0 9.3499697,14.604547 V 8.7685301 a 2.8057771,2.8057771 0 0 1 2.7422783,0.7457462 1.8326155,1.8326155 0 0 1 0.211172,0.320449 c 0,0.0089 0,0.02067 0,0.03544 z m 4.134829,4.6900777 v 0.01625 A 5.2851981,5.2851981 0 0 0 13.4848,15.100732 v -5.2305587 0 c 0.10337,-0.128475 0.199357,-0.258427 0.283531,-0.3411235 a 2.935729,2.935729 0 0 1 2.079229,-0.8579773 2.8929039,2.8929039 0 0 1 0.590689,0.060545 z',
    ],
  },
  CalendarToday: {
    viewBox: '0 0 24 24',
    path: 'M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z',
  },
  ChevronLeft: {
    viewBox: '0 0 36 36',
    path: 'M23.12 11.12L21 9l-9 9 9 9 2.12-2.12L16.24 18z',
  },
  ChevronRight: {
    viewBox: '0 0 36 36',
    path: 'M15 9l-2.12 2.12L19.76 18l-6.88 6.88L15 27l9-9z',
  },
  ChevronUp: {
    viewBox: '0 0 30 30',
    path: 'M27 18v2a1.002 1.002 0 01-1.634.774L15 12.292 4.633 20.774A1.001 1.001 0 013 20v-2a1 1 0 01.367-.774l11-9a1.002 1.002 0 011.267 0l11 9c.231.19.366.474.366.774z',
  },
  ChevronDown: {
    viewBox: '0 0 30 30',
    path: 'M3 12v-2a1.002 1.002 0 011.634-.774L15 17.708l10.367-8.482A1.001 1.001 0 0127 10v2a1 1 0 01-.367.774l-11 9a1.002 1.002 0 01-1.267 0l-11-9A1.003 1.003 0 013 12z',
  },
  ChromeReaderMode: {
    viewBox: '0 0 48 48',
    path: 'M26 24h14v3H26zm0-5h14v3H26zm0 10h14v3H26zM42 8H6c-2.2 0-4 1.8-4 4v26c0 2.2 1.8 4 4 4h36c2.2 0 4-1.8 4-4V12c0-2.2-1.8-4-4-4zm0 30H24V12h18v26z',
  },
  Close: {
    viewBox: '0 0 36 36',
    path: 'M28.5 9.62L26.38 7.5 18 15.88 9.62 7.5 7.5 9.62 15.88 18 7.5 26.38l2.12 2.12L18 20.12l8.38 8.38 2.12-2.12L20.12 18z',
  },
  CropPortrait: {
    viewBox: '0 0 48 48',
    path: 'M34 6H14c-2.21 0-4 1.79-4 4v28c0 2.21 1.79 4 4 4h20c2.21 0 4-1.79 4-4V10c0-2.21-1.79-4-4-4zm0 32H14V10h20v28z',
  },
  DirectionsBike: {
    viewBox: '0 0 48 48',
    path: 'M31 11c2.2 0 4-1.8 4-4s-1.8-4-4-4-4 1.8-4 4 1.8 4 4 4zM10 24C4.5 24 0 28.5 0 34s4.5 10 10 10 10-4.5 10-10-4.5-10-10-10zm0 17c-3.9 0-7-3.1-7-7s3.1-7 7-7 7 3.1 7 7-3.1 7-7 7zm11.6-19.9l4.7-4.8 1.5 1.5c2.5 2.6 6 4.1 10.1 4.1v-4c-3 0-5.5-1.1-7.3-2.9l-3.9-3.8c-.6-.7-1.6-1.2-2.7-1.2s-2.1.4-2.8 1.2l-5.5 5.5c-.7.7-1.2 1.7-1.2 2.8 0 1.1.5 2.1 1.2 2.9L22 28v10h4V25.5l-4.4-4.4zM38 24c-5.5 0-10 4.5-10 10s4.5 10 10 10 10-4.5 10-10-4.5-10-10-10zm0 17c-3.9 0-7-3.1-7-7s3.1-7 7-7 7 3.1 7 7-3.1 7-7 7z',
  },
  DirectionsBoat: {
    viewBox: '0 0 48 48',
    path: 'M40 42c-2.78 0-5.56-.94-8-2.65-4.88 3.42-11.12 3.42-16 0C13.56 41.06 10.78 42 8 42H4v4h4c2.75 0 5.48-.69 8-1.99 5.04 2.59 10.96 2.59 16 0C34.52 45.3 37.25 46 40 46h4v-4h-4zM7.89 38H8c3.2 0 6.05-1.76 8-4 1.95 2.24 4.8 4 8 4s6.05-1.76 8-4c1.96 2.24 4.79 4 8 4h.11l3.79-13.37c.17-.51.12-1.07-.12-1.55-.25-.48-.68-.84-1.2-.99L40 21.24V12c0-2.21-1.79-4-4-4h-6V2H18v6h-6c-2.21 0-4 1.79-4 4v9.24l-2.57.84c-.52.16-.95.51-1.2.99s-.29 1.04-.12 1.55L7.89 38zM12 12h24v7.93L24 16l-12 3.93V12z',
  },
  DirectionsCar: {
    viewBox: '0 0 48 48',
    path: 'M37.84 12.02C37.43 10.84 36.31 10 35 10H13c-1.31 0-2.43.84-2.84 2.02L6 24v16c0 1.1.9 2 2 2h2c1.11 0 2-.9 2-2v-2h24v2c0 1.1.9 2 2 2h2c1.11 0 2-.9 2-2V24l-4.16-11.98zM13 32c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm22 0c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zM10 22l3-9h22l3 9H10z',
  },
  Email: {
    viewBox: '0 0 24 24',
    path: [
      'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10h5v-2h-5c-4.34 0-8-3.66-8-8s3.66-8 8-8 8 3.66 8 8v1.43c0 .79-.71 1.57-1.5 1.57s-1.5-.78-1.5-1.57V12c0-2.76-2.24-5-5-5s-5 2.24-5 5 2.24 5 5 5c1.38 0 2.64-.56 3.54-1.47.65.89 1.77 1.47 2.96 1.47 1.97 0 3.5-1.6 3.5-3.57V12c0-5.52-4.48-10-10-10zm0 13c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z',
    ],
  },
  EmojiObjects: {
    viewBox: '0 0 24 24',
    path: 'M12,3c-0.46,0-0.93,0.04-1.4,0.14C7.84,3.67,5.64,5.9,5.12,8.66c-0.48,2.61,0.48,5.01,2.22,6.56 C7.77,15.6,8,16.13,8,16.69V19c0,1.1,0.9,2,2,2h0.28c0.35,0.6,0.98,1,1.72,1s1.38-0.4,1.72-1H14c1.1,0,2-0.9,2-2v-2.31 c0-0.55,0.22-1.09,0.64-1.46C18.09,13.95,19,12.08,19,10C19,6.13,15.87,3,12,3z M14,17h-4v-1h4V17z M10,19v-1h4v1H10z M15.31,13.74c-0.09,0.08-0.16,0.18-0.24,0.26H8.92c-0.08-0.09-0.15-0.19-0.24-0.27c-1.32-1.18-1.91-2.94-1.59-4.7 c0.36-1.94,1.96-3.55,3.89-3.93C11.32,5.03,11.66,5,12,5c2.76,0,5,2.24,5,5C17,11.43,16.39,12.79,15.31,13.74z',
  },
  ExpandLess: {
    viewBox: '0 0 36 36',
    path: 'M18 12l-9 9 2.12 2.12L18 16.24l6.88 6.88L27 21z',
  },
  ExpandMore: {
    viewBox: '0 0 36 36',
    path: 'M24.88 12.88L18 19.76l-6.88-6.88L9 15l9 9 9-9z',
  },
  ExternalLink: {
    viewBox: '0 0 512 512',
    path: 'M432,320H400a16,16,0,0,0-16,16V448H64V128H208a16,16,0,0,0,16-16V80a16,16,0,0,0-16-16H48A48,48,0,0,0,0,112V464a48,48,0,0,0,48,48H400a48,48,0,0,0,48-48V336A16,16,0,0,0,432,320ZM488,0h-128c-21.37,0-32.05,25.91-17,41l35.73,35.73L135,320.37a24,24,0,0,0,0,34L157.67,377a24,24,0,0,0,34,0L435.28,133.32,471,169c15,15,41,4.5,41-17V24A24,24,0,0,0,488,0Z',
  },
  Facebook: {
    viewBox: '0 0 30 30',
    path: 'M22,16l1-5l-5,0.001V7c0-1.544,0.784-2,3-2h2V0c0,0-2.05,0-4,0c-4.072,0-7,2.435-7,7v4l-5,0v5h5v14h6V16H22z',
  },
  Favourite: {
    viewBox: '0 0 48 48',
    path: 'M24 42.7l-2.9-2.63C10.8 30.72 4 24.55 4 17 4 10.83 8.83 6 15 6c3.48 0 6.82 1.62 9 4.17C26.18 7.62 29.52 6 33 6c6.17 0 11 4.83 11 11 0 7.55-6.8 13.72-17.1 23.07L24 42.7z',
  },
  FingerPrint: {
    viewBox: '0 0 140 140',
    path: 'M102.5,74.53a24.26,24.26,0,0,0-39.74,27.83,65.73,65.73,0,0,0,12.38,13.31,47.27,47.27,0,0,1-14.74-5.32c-1.1-1.34-2.14-2.72-3.14-4.14A31,31,0,0,1,108,70.68h0a10.32,10.32,0,0,0,3,2.86,51.68,51.68,0,0,1,.82,7.63,17.1,17.1,0,0,1-9.31-6.64ZM110.07,95a30.83,30.83,0,0,1-18.88-12.5h0a10.45,10.45,0,0,0-17.12,12,51.48,51.48,0,0,0,22.35,18,28.71,28.71,0,0,1-8.73,3.18A58.34,58.34,0,0,1,68.57,98.29,17.16,17.16,0,0,1,96.69,78.6h0a24.1,24.1,0,0,0,14.78,9.79,39.47,39.47,0,0,1-1.4,6.56ZM103,107.34a44.57,44.57,0,0,1-23.11-17,3.36,3.36,0,0,1,5.5-3.85,37.86,37.86,0,0,0,21.85,15,29.57,29.57,0,0,1-4.24,5.77Zm-63.41-16c-.58-.85-1.15-1.7-1.7-2.57A44.73,44.73,0,0,1,99,46.8a67.2,67.2,0,0,1,7.08,11.68,38.07,38.07,0,0,0-60,40.81,67.79,67.79,0,0,1-6.56-8Zm-10.76-26A58.51,58.51,0,0,1,80.1,29.93a58.89,58.89,0,0,1,10.06,7.22A51.78,51.78,0,0,0,32,77.17a58.9,58.9,0,0,1-3.22-11.85Zm10.94-35.2c.49-.39,1-.77,1.52-1.14s1.21-.81,1.84-1.18c6.91-4.07,15.21-5,23.66-3.09A65.85,65.85,0,0,0,28.6,51.08c1.2-8.51,4.94-15.94,11.16-21Zm58.9-24a70,70,0,1,0,35.18,92.5A70,70,0,0,0,98.66,6.16Z',
  },
  FollowTheSign: {
    viewBox: '0 0 24 24',
    path: 'M9.5,5.5c1.1,0,2-0.9,2-2s-0.9-2-2-2s-2,0.9-2,2S8.4,5.5,9.5,5.5z M5.75,8.9L3,23h2.1l1.75-8L9,17v6h2v-7.55L8.95,13.4 l0.6-3C10.85,12,12.8,13,15,13v-2c-1.85,0-3.45-1-4.35-2.45L9.7,6.95C9.35,6.35,8.7,6,8,6C7.75,6,7.5,6.05,7.25,6.15L2,8.3V13h2 V9.65L5.75,8.9 M13,2v7h3.75v14h1.5V9H22V2H13z M18.01,8V6.25H14.5v-1.5h3.51V3l2.49,2.5L18.01,8z',
  },
  Hamburger: {
    viewBox: '0 0 36 36',
    path: 'M4 27h28v-3H4v3zm0-8h28v-3H4v3zM4 8v3h28V8H4z',
  },
  HelpOutline: {
    viewBox: '0 0 24 24',
    path: 'M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z',
  },
  Home: {
    viewBox: '0 0 48 48',
    path: 'M20 40V28h8v12h10V24h6L24 6 4 24h6v16z',
  },
  Instagram: {
    viewBox: '0 0 30 30',
    path: 'M26,0C26,0,3.529,0,3.5,0C1.582,0,0.027,1.586,0.004,3.5L0,3.458c0,0,0,23.028,0,23.042    C0,28.434,1.567,30,3.5,30c0.014,0,23,0,23,0c1.873-0.067,3.5-1.609,3.5-3.5c0-0.014,0-23,0-23C29.978,1.629,27.859,0.067,26,0z     M15,9.5c3.038,0,5.5,2.463,5.5,5.5c0,3.036-2.462,5.5-5.5,5.5c-3.037,0-5.5-2.464-5.5-5.5C9.5,11.963,11.963,9.5,15,9.5z M26,24    c0,1.104-0.896,2-2,2H6c-1.104,0-2-0.896-2-2V12.5h2.359C6.13,13.294,6,14.132,6,15c0,4.97,4.031,9,9,9c4.972,0,9-4.03,9-9    c0-0.868-0.13-1.706-0.359-2.5H26l-0.004,11.461C25.996,23.975,26,23.986,26,24z M26,5.008v3.016h-0.005    c-0.013,0.535-0.442,0.965-0.979,0.974V9H22c-0.545,0-0.982-0.436-0.995-0.977H21V5.008h0.002C21.002,5.005,21,5.003,21,5    c0-0.552,0.447-1,1-1h3.016v0.003C25.561,4.012,26,4.453,26,5c0,0.003-0.002,0.005-0.002,0.008H26z',
  },
  Lock: {
    viewBox: '0 0 24 24',
    path: 'm18.75 9h-.75v-3c0-3.309-2.691-6-6-6s-6 2.691-6 6v3h-.75c-1.24 0-2.25 1.009-2.25 2.25v10.5c0 1.241 1.01 2.25 2.25 2.25h13.5c1.24 0 2.25-1.009 2.25-2.25v-10.5c0-1.241-1.01-2.25-2.25-2.25zm-10.75-3c0-2.206 1.794-4 4-4s4 1.794 4 4v3h-8zm5 10.722v2.278c0 .552-.447 1-1 1s-1-.448-1-1v-2.278c-.595-.347-1-.985-1-1.722 0-1.103.897-2 2-2s2 .897 2 2c0 .737-.405 1.375-1 1.722z',
  },
  LocalFlorist: {
    viewBox: '0 0 48 48',
    path: 'M24 44c9.94 0 18-8.06 18-18-9.94 0-18 8.06-18 18zM11.21 20.5c0 2.76 2.24 5 5 5 1.05 0 2.03-.33 2.83-.88L19 25c0 2.76 2.24 5 5 5s5-2.24 5-5l-.04-.38c.81.56 1.78.88 2.83.88 2.76 0 5-2.24 5-5 0-1.99-1.17-3.7-2.86-4.5 1.68-.8 2.86-2.51 2.86-4.5 0-2.76-2.24-5-5-5-1.05 0-2.03.33-2.83.88L29 7c0-2.76-2.24-5-5-5s-5 2.24-5 5l.04.38c-.81-.56-1.78-.88-2.83-.88-2.76 0-5 2.24-5 5 0 1.99 1.17 3.7 2.86 4.5-1.69.8-2.86 2.51-2.86 4.5zM24 11c2.76 0 5 2.24 5 5s-2.24 5-5 5-5-2.24-5-5 2.24-5 5-5zM6 26c0 9.94 8.06 18 18 18 0-9.94-8.06-18-18-18z',
  },
  Loyalty: {
    viewBox: '0 0 48 48',
    path: 'M42.82 23.16L24.83 5.17C24.11 4.45 23.11 4 22 4H8C5.79 4 4 5.79 4 8v14c0 1.11.45 2.11 1.18 2.83l18 18C23.9 43.55 24.9 44 26 44c1.11 0 2.11-.45 2.83-1.17l14-14C43.55 28.1 44 27.1 44 26c0-1.11-.45-2.11-1.18-2.84zM11 14c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm23.54 16.54L26 39.08l-8.54-8.54C16.56 29.63 16 28.38 16 27c0-2.76 2.24-5 5-5 1.38 0 2.64.56 3.54 1.47L26 24.93l1.46-1.46C28.37 22.56 29.62 22 31 22c2.76 0 5 2.24 5 5 0 1.38-.56 2.63-1.46 3.54z',
  },
  Magazine: {
    viewBox: '0 0 465 465',
    path: [
      'M240,356.071V132.12c0-4.143-3.357-7.5-7.5-7.5s-7.5,3.357-7.5,7.5v223.951c0,4.143,3.357,7.5,7.5,7.5 S240,360.214,240,356.071z',
      'M457.5,75.782c-15.856,0-35.614-6.842-56.533-14.085c-26.492-9.174-56.521-19.571-87.663-19.571	c-36.035,0-58.019,15.791-70.115,29.038c-4.524,4.956-8.03,9.922-10.688,14.327c-2.658-4.405-6.164-9.371-10.688-14.327	c-12.097-13.247-34.08-29.038-70.115-29.038c-31.143,0-61.171,10.397-87.663,19.571C43.114,68.94,23.356,75.782,7.5,75.782 c-4.143,0-7.5,3.357-7.5,7.5v302.092c0,4.143,3.357,7.5,7.5,7.5c18.38,0,39.297-7.243,61.441-14.911	c25.375-8.786,54.136-18.745,82.755-18.745c24.54,0,44.403,8.126,59.038,24.152c2.792,3.058,7.537,3.273,10.596,0.48 s3.273-7.537,0.48-10.596c-12.097-13.246-34.08-29.037-70.114-29.037c-31.143,0-61.171,10.397-87.663,19.571	C46.298,369.931,29.396,375.782,15,377.422V90.41c16.491-1.571,34.755-7.896,53.941-14.539	c25.375-8.786,54.136-18.745,82.755-18.745c57.881,0,73.025,45.962,73.634,47.894c0.968,3.148,3.876,5.298,7.17,5.298	s6.202-2.149,7.17-5.298c0.146-0.479,15.383-47.894,73.634-47.894c28.619,0,57.38,9.959,82.755,18.745	c19.187,6.644,37.45,12.968,53.941,14.539v287.012c-14.396-1.64-31.298-7.491-49.033-13.633	c-26.492-9.174-56.521-19.571-87.663-19.571c-36.036,0-58.02,15.791-70.115,29.038c-2.793,3.06-2.578,7.803,0.48,10.596	c3.06,2.793,7.804,2.578,10.596-0.48c14.635-16.027,34.498-24.153,59.039-24.153c28.619,0,57.38,9.959,82.755,18.745	c22.145,7.668,43.062,14.911,61.441,14.911c4.143,0,7.5-3.357,7.5-7.5V83.282C465,79.14,461.643,75.782,457.5,75.782z',
      'M457.5,407.874c-15.856,0-35.614-6.842-56.533-14.085c-26.492-9.174-56.521-19.571-87.663-19.571 c-33.843,0-55.291,13.928-67.796,26.596l-26.017-0.001c-12.505-12.668-33.954-26.595-67.795-26.595 c-31.143,0-61.171,10.397-87.663,19.571c-20.919,7.243-40.677,14.085-56.533,14.085c-4.143,0-7.5,3.357-7.5,7.5s3.357,7.5,7.5,7.5 c18.38,0,39.297-7.243,61.441-14.911c25.375-8.786,54.136-18.745,82.755-18.745c24.54,0,44.403,8.126,59.038,24.152 c1.421,1.556,3.431,2.442,5.538,2.442l32.454,0.001c2.107,0,4.117-0.887,5.538-2.442c14.635-16.027,34.498-24.153,59.039-24.153 c28.619,0,57.38,9.959,82.755,18.745c22.145,7.668,43.062,14.911,61.441,14.911c4.143,0,7.5-3.357,7.5-7.5	S461.643,407.874,457.5,407.874z',
    ],
  },
  MoreHorizontal: {
    viewBox: '0 0 36 36',
    path: 'M9 15c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3zm18 0c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3zm-9 0c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z',
  },
  MoreVertical: {
    viewBox: '0 0 36 36',
    path: 'M18 12c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm0 3c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3zm0 9c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z',
  },
  Pencil: {
    viewBox: '0 0 492.493 492',
    path: [
      'M304.14 82.473L33.165 353.469a10.799 10.799 0 00-2.816 4.949L.313 478.973a10.716 10.716 0 002.816 10.136 10.675 10.675 0 007.527 3.114 10.6 10.6 0 002.582-.32l120.555-30.04a10.655 10.655 0 004.95-2.812l271-270.977zm0 0M476.875 45.523L446.711 15.36c-20.16-20.16-55.297-20.14-75.434 0l-36.949 36.95 105.598 105.597 36.949-36.949c10.07-10.066 15.617-23.465 15.617-37.715s-5.547-27.648-15.617-37.719zm0 0',
    ],
  },
  Pinterest: {
    viewBox: '0 0 30 30',
    path: 'M16,0C7.813,0,3,6.105,3,11c0,2.964,2,6,3,6s2,0,2-1s-2-2-2-5c0-4.354,4.773-8,10-8c4.627,0,7,3.224,7,7  c0,4.968-2.735,9-6,9c-1.803,0-3.433-1.172-3-3c0.519-2.184,1-2,2-6c0.342-1.368-0.433-3-2-3c-1.843,0-4,1.446-4,4c0,1.627,1,3,1,3  s-2.245,7.863-2.576,9.263C7.766,26.049,6.938,30,7.938,30S10,28,12,23c0.295-0.738,1-3,1-3c0.599,1.142,3.14,2,5,2  c5.539,0,9-5.24,9-12C27,4.888,22.58,0,16,0z',
  },
  RemoveRedEye: {
    viewBox: '0 0 48 48',
    path: 'M24 9C14 9 5.46 15.22 2 24c3.46 8.78 12 15 22 15s18.54-6.22 22-15C42.54 15.22 34.01 9 24 9zm0 25c-5.52 0-10-4.48-10-10s4.48-10 10-10 10 4.48 10 10-4.48 10-10 10zm0-16c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6z',
  },
  Search: {
    viewBox: '0 0 48 48',
    path: 'M31 28h-1.59l-.55-.55C30.82 25.18 32 22.23 32 19c0-7.18-5.82-13-13-13S6 11.82 6 19s5.82 13 13 13c3.23 0 6.18-1.18 8.45-3.13l.55.55V31l10 9.98L40.98 38 31 28zm-12 0c-4.97 0-9-4.03-9-9s4.03-9 9-9 9 4.03 9 9-4.03 9-9 9z',
  },
  SeriesOfTubes: {
    viewBox: '0 0 512.08 512.08',
    path: [
      'm 244.03999,4.6479998 h 24 V 494.56801 h -24 z',
      'm 11.672,244.03999 h 493.62399 v 24 H 11.672 Z',
      'M255.752,196.688c-128.36,0-179.528-106.68-181.64-111.216l21.752-10.144L84.992,80.4l10.864-5.088  c1.872,3.976,47.152,97.376,159.896,97.376c113.168,0,158.168-95.272,158.608-96.232l21.816,10  C434.112,90.952,384.16,196.688,255.752,196.688z',
      'M95.864,436.752l-21.752-10.144c2.112-4.536,53.28-111.216,181.64-111.216c128.408,0,178.36,105.736,180.424,110.232  l-21.816,10c-1.816-3.904-45.928-96.232-158.608-96.232C142.528,339.392,96.312,435.776,95.864,436.752z',
      'M242.648,510.696c-4.848-4.44-118.752-110.36-118.752-254.656c0-144.512,116.504-246.512,121.472-250.792l15.664,18.176  c-1.136,0.984-113.136,99.296-113.136,232.616c0,133.832,109.84,235.936,110.952,236.952L242.648,510.696z',
      'M269.432,510.696L253.224,493c1.112-1.024,110.96-103.416,110.96-236.96c0-133.624-112-231.624-113.128-232.6l15.656-18.184  c4.96,4.272,121.472,106.272,121.472,250.784C388.184,400.336,274.28,506.256,269.432,510.696z',
      'M256.04,512.08c-141.16,0-256-114.864-256-256.04S114.88,0,256.04,0s256,114.864,256,256.04S397.2,512.08,256.04,512.08z   M256.04,24c-127.92,0-232,104.096-232,232.04s104.08,232.04,232,232.04s232-104.096,232-232.04S383.96,24,256.04,24z',
    ],
  },
  Share: {
    viewBox: '0 0 55.94 55.82',
    path: 'M55.94 21.63a5.3 5.3 0 01-1.84 3q-7.92 7.89-15.8 15.8a18.45 18.45 0 01-1.59 1.47c-1.42 1.12-2.69.6-2.94-1.19a8 8 0 01-.08-1.2V29.27h-.93a41.59 41.59 0 00-11.8 1.45 23.69 23.69 0 00-8.91 4.6 17.88 17.88 0 00-6 10.16 14.43 14.43 0 00.19 7.38c.12.39.3.77.43 1.16a2 2 0 01.12.63 1.12 1.12 0 01-.55 1 1.23 1.23 0 01-1.37-.18 4.77 4.77 0 01-1-1.22A29 29 0 01.24 43.14 26 26 0 012.39 29a25.3 25.3 0 018.5-10.12 32.07 32.07 0 0111-4.91 45.42 45.42 0 0110.35-1.15h1.51v-1.41-8.9a5.67 5.67 0 01.11-1A1.8 1.8 0 0135.03 0h.49a4.5 4.5 0 012 1.28q2.19 2.2 4.4 4.39Q48 11.74 54.07 17.81a5.58 5.58 0 011.88 2.94z',
  },
  ShoppingBasket: {
    viewBox: '0 0 48 48',
    path: 'M34.42 18L25.66 4.89c-.38-.58-1.02-.85-1.66-.85-.64 0-1.28.28-1.66.85L13.58 18H4c-1.1 0-2 .9-2 2 0 .19.03.37.07.54l5.07 18.54C7.61 40.76 9.16 42 11 42h26c1.84 0 3.39-1.24 3.85-2.93l5.07-18.54c.05-.16.08-.34.08-.53 0-1.1-.9-2-2-2h-9.58zM18 18l6-8.8 6 8.8H18zm6 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z',
  },
  SimpleMagazine: {
    // attribution: "Gregor Cresnar, the Noun Project",
    viewBox: '0 0 100 100',
    path: 'M80,21a27.88,27.88,0,0,0-29.76,6.23,27,27,0,0,0-30.5-5.69A3,3,0,0,0,18,24.3V74a2.76,2.76,0,0,0,3.72,2.6,27,27,0,0,1,24.47,3,7.37,7.37,0,0,0,4.16,1.28,7.3,7.3,0,0,0,4.07-1.23,27.87,27.87,0,0,1,24-3.42,2.7,2.7,0,0,0,.82.13A2.78,2.78,0,0,0,82,73.58V23.82A3,3,0,0,0,80,21ZM46,35.24V70.3a35,35,0,0,0-20-3V27.78a19,19,0,0,1,18.57,5.05A12.41,12.41,0,0,1,46,35C46,35.06,46,35.14,46,35.24ZM74,67v.11a35.79,35.79,0,0,0-20,3.55V35.24h0c.7-.87,1.35-1.75,1.92-2.31A19.88,19.88,0,0,1,70,27.12a19.59,19.59,0,0,1,4,.41Z',
  },
  StoreFront: {
    viewBox: '0 0 24 24',
    path: 'M21.9,8.89l-1.05-4.37c-0.22-0.9-1-1.52-1.91-1.52H5.05C4.15,3,3.36,3.63,3.15,4.52L2.1,8.89 c-0.24,1.02-0.02,2.06,0.62,2.88C2.8,11.88,2.91,11.96,3,12.06V19c0,1.1,0.9,2,2,2h14c1.1,0,2-0.9,2-2v-6.94 c0.09-0.09,0.2-0.18,0.28-0.28C21.92,10.96,22.15,9.91,21.9,8.89z M18.91,4.99l1.05,4.37c0.1,0.42,0.01,0.84-0.25,1.17 C19.57,10.71,19.27,11,18.77,11c-0.61,0-1.14-0.49-1.21-1.14L16.98,5L18.91,4.99z M13,5h1.96l0.54,4.52 c0.05,0.39-0.07,0.78-0.33,1.07C14.95,10.85,14.63,11,14.22,11C13.55,11,13,10.41,13,9.69V5z M8.49,9.52L9.04,5H11v4.69 C11,10.41,10.45,11,9.71,11c-0.34,0-0.65-0.15-0.89-0.41C8.57,10.3,8.45,9.91,8.49,9.52z M4.04,9.36L5.05,5h1.97L6.44,9.86 C6.36,10.51,5.84,11,5.23,11c-0.49,0-0.8-0.29-0.93-0.47C4.03,10.21,3.94,9.78,4.04,9.36z M5,19v-6.03C5.08,12.98,5.15,13,5.23,13 c0.87,0,1.66-0.36,2.24-0.95c0.6,0.6,1.4,0.95,2.31,0.95c0.87,0,1.65-0.36,2.23-0.93c0.59,0.57,1.39,0.93,2.29,0.93 c0.84,0,1.64-0.35,2.24-0.95c0.58,0.59,1.37,0.95,2.24,0.95c0.08,0,0.15-0.02,0.23-0.03V19H5z',
  },
  StoreMallDirectory: {
    viewBox: '0 0 48 48',
    path: 'M40 8H8v4h32V8zm2 20v-4l-2-10H8L6 24v4h2v12h20V28h8v12h4V28h2zm-18 8H12v-8h12v8z',
  },
  ViewComfy: {
    viewBox: '0 0 48 48',
    path: 'M6 18h8v-8H6v8zm0 10h8v-8H6v8zm10 0h8v-8h-8v8zm10 0h8v-8h-8v8zM16 18h8v-8h-8v8zm10-8v8h8v-8h-8zm10 18h8v-8h-8v8zM6 38h8v-8H6v8zm10 0h8v-8h-8v8zm10 0h8v-8h-8v8zm10 0h8v-8h-8v8zm0-28v8h8v-8h-8z',
  },
  GridLarge: {
    viewBox: '0 0 22 22',
    path: 'M4,2H20A2,2 0 0,1 22,4V20A2,2 0 0,1 20,22H4C2.92,22 2,21.1 2,20V4A2,2 0 0,1 4,2M4,4V11H11V4H4M4,20H11V13H4V20M20,20V13H13V20H20M20,4H13V11H20V4Z',
  },
  HidePassword: {
    viewBox: '0 0 223.47 223.47',
    path: 'M222.46,109.33a128.35,128.35,0,0,1-110.73,63.93,7.5,7.5,0,0,1,0-15,113.33,113.33,0,0,0,95.48-52.69,114.53,114.53,0,0,0-30.29-31.95,7.5,7.5,0,0,1,8.69-12.23,129.33,129.33,0,0,1,36.85,40.43A7.47,7.47,0,0,1,222.46,109.33Zm-66.2-3.76a45.12,45.12,0,0,0-1-9.64,7.5,7.5,0,1,0-14.65,3.24,29.53,29.53,0,0,1-28.83,35.92,29.23,29.23,0,0,1-14.87-4L205.37,22.54a7.5,7.5,0,0,0-10.6-10.6L86.25,120.45a29.52,29.52,0,0,1,32.37-43.59,7.5,7.5,0,0,0,3.49-14.59,44.5,44.5,0,0,0-46.69,69L60.66,146.05a112.93,112.93,0,0,1-44.4-40.48A113,113,0,0,1,135.51,55.39a7.5,7.5,0,1,0,3.15-14.66A128,128,0,0,0,1,101.82a7.47,7.47,0,0,0,0,7.51,127.87,127.87,0,0,0,48.56,47.8l-43.8,43.8a7.5,7.5,0,0,0,10.61,10.61L86,141.89a44.49,44.49,0,0,0,70.23-36.32Z',
  },
  ShowPassword: {
    viewBox: '0 0 512 512',
    path: 'M507.42,241.38c-3.95-5.67-98.42-139-251.42-139S8.53,235.71,4.58,241.38a25.61,25.61,0,0,0,0,29.24c3.95,5.67,98.42,139,251.42,139s247.47-133.31,251.42-139A25.61,25.61,0,0,0,507.42,241.38ZM256,384C114.62,384,25.6,256,25.6,256s89-128,230.4-128S486.4,256,486.4,256,397.38,384,256,384Zm0-230.4A102.4,102.4,0,1,0,358.4,256,102.4,102.4,0,0,0,256,153.6Zm0,179.2A76.8,76.8,0,1,1,332.8,256,76.88,76.88,0,0,1,256,332.8Z',
  },
  GridSmall: {
    viewBox: '0 0 22 22',
    path: 'M10,4V8H14V4H10M16,4V8H20V4H16M16,10V14H20V10H16M16,16V20H20V16H16M14,20V16H10V20H14M8,20V16H4V20H8M8,14V10H4V14H8M8,8V4H4V8H8M10,14H14V10H10V14M4,2H20A2,2 0 0,1 22,4V20A2,2 0 0,1 20,22H4C2.92,22 2,21.1 2,20V4A2,2 0 0,1 4,2Z',
  },
  Square: {
    viewBox: '0 0 22 22',
    path: 'M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M19,5V19H5V5H19Z',
  },
  WhatsHot: {
    viewBox: '0 0 48 48',
    path: 'M27 1.34s1.48 5.3 1.48 9.6c0 4.12-2.7 7.47-6.83 7.47s-7.25-3.34-7.25-7.47l.05-.72C10.43 15.03 8 21.23 8 28c0 8.84 7.16 16 16 16s16-7.16 16-16c0-10.79-5.19-20.41-13-26.66zM23.42 38c-3.56 0-6.45-2.81-6.45-6.28 0-3.25 2.09-5.53 5.63-6.24s7.2-2.41 9.23-5.15c.78 2.58 1.19 5.3 1.19 8.07 0 5.29-4.3 9.6-9.6 9.6z',
  },
  BookMark: {
    viewBox: '0 0 423.936 423.936',
    path: 'M327.68,0H96.256c-22.528,0-40.96,18.432-40.96,40.96v357.376c0,9.728,5.632,18.944,14.336,23.04 c9.216,4.096,19.456,3.072,27.136-3.072l0.512-0.512l114.688-96.768l114.688,96.768l0.512,0.512 c4.608,3.584,10.24,5.632,15.872,5.632c3.584,0,7.68-1.024,11.264-3.072c8.704-4.096,14.336-13.312,14.336-23.04V40.96 C368.64,18.432,350.208,0,327.68,0z',
  },
  Plus: {
    viewBox: '0 0 448 448',
    path: 'M408 184h-136c-4.417969 0-8-3.582031-8-8v-136c0-22.089844-17.910156-40-40-40s-40 17.910156-40 40v136c0 4.417969-3.582031 8-8 8h-136c-22.089844 0-40 17.910156-40 40s17.910156 40 40 40h136c4.417969 0 8 3.582031 8 8v136c0 22.089844 17.910156 40 40 40s40-17.910156 40-40v-136c0-4.417969 3.582031-8 8-8h136c22.089844 0 40-17.910156 40-40s-17.910156-40-40-40zm0 0',
  },
  Trash: {
    viewBox: '0 0 298.7 384',
    path: 'M21.3,341.3c0,23.6,19.1,42.7,42.7,42.7h170.7c23.6,0,42.7-19.1,42.7-42.7v-256h-256V341.3z M224,21.3L202.7,0 H96L74.7,21.3H0V64h298.7V21.3H224z',
  },

  // CARD LOGOS
  visa: {
    viewBox: '0 0 50 50',
    path: 'M5 7c-2.758 0-5 2.242-5 5v26c0 2.758 2.242 5 5 5h40c2.758 0 5-2.242 5-5V12c0-2.758-2.242-5-5-5zm24.688 12.406c1.363 0 2.78.563 2.78.563l-.5 2.437s-1.077-.718-2.03-.718c-1.47 0-2 .48-2 1.125 0 1.261 4.093 1.484 4.093 4.312 0 2.352-2.918 3.969-5.031 3.969-2.113 0-3.219-.625-3.219-.625l.532-2.375s1.105.656 2.812.656c1.703 0 1.938-.727 1.938-1.031 0-1.805-4.063-1.301-4.063-4.813 0-1.941 1.586-3.5 4.688-3.5zm-13.22.219h3.157l-4.5 11.125h-3.25l-2.375-9s2.496 1.273 4.031 4.719c.067.418.25 1.093.25 1.093zm4.313 0h3L22 30.75h-2.969zm16.032 0h3.093l2.282 11.125H39.5l-.313-1.594H35.5l-.594 1.594H31.97zm-30.563.031h4.563c1.164 0 1.593 1.094 1.593 1.094l1 5.063c-1.351-4.36-7.156-6.157-7.156-6.157zm31.688 3.188l-1.626 4.187h2.438z',
  },
  mastercard: {
    viewBox: '0 0 50 50',
    path: 'M5 7c-2.75 0-5 2.25-5 5v26c0 2.75 2.25 5 5 5h40c2.75 0 5-2.25 5-5V12c0-2.75-2.25-5-5-5zm0 2h40c1.668 0 3 1.332 3 3v26c0 1.668-1.332 3-3 3H5c-1.668 0-3-1.332-3-3V12c0-1.668 1.332-3 3-3zm12 4c-6.617 0-12 5.383-12 12s5.383 12 12 12c3.078 0 5.875-1.184 8-3.094C27.125 35.816 29.926 37 33 37c6.617 0 12-5.383 12-12s-5.383-12-12-12c-3.074 0-5.875 1.184-8 3.094C22.875 14.184 20.078 13 17 13zm16 2c5.535 0 10 4.465 10 10s-4.465 10-10 10c-2.55 0-4.89-.934-6.656-2.5C27.996 30.441 29 27.84 29 25s-1.004-5.441-2.656-7.5C28.109 15.934 30.449 15 33 15z',
  },
  amex: {
    viewBox: '0 0 50 50',
    path: 'M5 7c-2.758 0-5 2.242-5 5v26c0 2.758 2.242 5 5 5h40c2.758 0 5-2.242 5-5V12c0-2.758-2.242-5-5-5zm3.563 12.906h2.78L15 28.094v-8.188h3.281l2.563 5.719 2.562-5.719h3.344v8.5h-2.094v-6.218l-2.843 6.218h-1.907l-2.812-6.312v6.312h-4.219l-.813-1.875H7.875l-.813 1.875H4.845zm20.937 0h8.25l2.313 2.657 2.406-2.657h2.656l-3.75 4.157 3.75 4.375h-2.656L40 25.593l-2.531 2.812H29.5zM9.906 21.688l-1.25 3h2.563zm21.657.03v1.595h4.625v1.625h-4.626v1.687h4.844l2.282-2.563-2.032-2.312z',
  },
  maestro: {
    viewBox: '0 0 50 50',
    path: 'M15 10C6.73 10 0 16.73 0 25s6.73 15 15 15a14.92 14.92 0 0010-3.844C27.656 38.536 31.16 40 35 40c8.27 0 15-6.73 15-15s-6.73-15-15-15c-3.84 0-7.344 1.465-10 3.844A14.92 14.92 0 0015 10zm20 2c7.168 0 13 5.832 13 13s-5.832 13-13 13a12.926 12.926 0 01-8.75-3.406c.371-.528.809-1.067 1.25-1.594h-2.719a12.952 12.952 0 01-.718-1h4.03c.4-.602.7-1.3 1-2H23a12.235 12.235 0 01-.375-1h6.688c.199-.602.273-1.3.375-2h-7.532c-.05-.328-.066-.664-.093-1h7.843v-2h-7.843c.027-.336.042-.672.093-1h7.75c-.101-.7-.207-1.398-.406-2h-6.875c.11-.344.238-.672.375-1h6c-.3-.7-.602-1.398-1-2h-3.938c.223-.348.465-.676.72-1h2.624a11.741 11.741 0 00-1.218-1.5C28.508 13.344 31.59 12 35 12z',
  },
  DebitCard: {
    viewBox: '0 0 50 50',
    path: 'M43 9H7c-2.758 0-5 2.242-5 5v22c0 2.758 2.242 5 5 5h36c2.758 0 5-2.242 5-5V14c0-2.758-2.242-5-5-5zM8 18c0-.55.45-1 1-1h4c.55 0 1 .45 1 1v3c0 .55-.45 1-1 1H9c-.55 0-1-.45-1-1zm1 10h5a1 1 0 110 2H9a1 1 0 110-2zm13 7H9a1 1 0 110-2h13a1 1 0 110 2zm1-5h-5a1 1 0 110-2h5a1 1 0 110 2zm9 0h-5c-.555 0-1-.445-1-1 0-.555.445-1 1-1h5c.555 0 1 .445 1 1 0 .555-.445 1-1 1zm9 0h-5c-.555 0-1-.445-1-1 0-.555.445-1 1-1h5c.555 0 1 .445 1 1 0 .555-.445 1-1 1zm0-10H28a1 1 0 110-2h13a1 1 0 110 2z',
  },
  RichTextBulletPoint: {
    viewBox: '0 0 30 30',
    path: 'M6 5a2 2 0 00-2 2 2 2 0 002 2 2 2 0 002-2 2 2 0 00-2-2zm6 1a1 1 0 100 2h13a1 1 0 100-2H12zm-6 7a2 2 0 00-2 2 2 2 0 002 2 2 2 0 002-2 2 2 0 00-2-2zm6 1a1 1 0 100 2h13a1 1 0 100-2H12zm-6 7a2 2 0 00-2 2 2 2 0 002 2 2 2 0 002-2 2 2 0 00-2-2zm6 1a1 1 0 100 2h13a1 1 0 100-2H12z',
  },
  RichTextBold: {
    viewBox: '0 0 30 30',
    path: 'M7 4a1 1 0 100 2h1v18H7a1 1 0 100 2h11.5c3.584 0 6.5-2.916 6.5-6.5 0-2.631-1.573-4.896-3.826-5.918A5.48 5.48 0 0023 9.5C23 6.467 20.532 4 17.5 4H7zm6 2h2.5C17.43 6 19 7.57 19 9.5S17.43 13 15.5 13H13V6zm0 9h2.5c2.481 0 4.5 2.019 4.5 4.5S17.981 24 15.5 24H13v-9z',
  },
  RichTextItalic: {
    viewBox: '0 0 30 30',
    path: 'M15.986 3.979a1.5 1.5 0 00-.224.021H13a1 1 0 100 2h1.44l-1.895 18H11a1 1 0 100 2h6a1 1 0 100-2h-1.44l1.895-18H19a1 1 0 100-2h-2.756a1.5 1.5 0 00-.258-.021z',
  },
  RichTextParagraph: {
    viewBox: '0 0 30 30',
    path: 'M20.984 3.986A1 1 0 0020.84 4H11a6 6 0 100 12h4v9a1 1 0 102 0V6h3v19a1 1 0 102 0V6h1a1 1 0 100-2h-1.846a1 1 0 00-.17-.014z',
  },
  RichTextUndo: {
    viewBox: '0 0 30 30',
    path: 'M10 3L4 9l6 6v-4h7c2.785 0 5 2.215 5 5s-2.215 5-5 5h-3a2 2 0 100 4h3c4.675 0 8.431-3.658 8.844-8.23A2 2 0 0026 16c0-4.947-4.053-9-9-9h-7V3z',
  },
  RichTextRedo: {
    viewBox: '0 0 30 30',
    path: 'M20 3v4h-7c-4.947 0-9 4.053-9 9a2 2 0 00.158.78 2 2 0 000 .001C4.577 21.348 8.33 25 13 25h3a2 2 0 100-4h-3a4.97 4.97 0 01-5-5c0-2.785 2.215-5 5-5h7v4l6-6-6-6z',
  },
  Upload: {
    viewBox: '0 0 24 24',
    path: 'M12 2a1 1 0 00-.707.295L6.16 7.135a.5.5 0 00-.012.01l-.002.001A.5.5 0 006 7.5a.5.5 0 00.5.5H10v8a1 1 0 001 1h2a1 1 0 001-1V8h3.5a.5.5 0 00.5-.5.5.5 0 00-.146-.354l-.032-.029-5.105-4.814a1 1 0 00-.033-.032A1 1 0 0012 2zM3 20a1 1 0 100 2h18a1 1 0 100-2H3z',
  },

  // Map number icon
  Number1: {
    viewBox: '0 0 303 433',
    path: 'M17.89 10.086L17.2965 8.42783L15.7447 8.9832L16.1961 10.5684L17.89 10.086ZM45.4395 106.83L43.7456 107.312L44.2144 108.958L45.8732 108.537L45.4395 106.83ZM123.603 86.9684H125.364V84.7038L123.17 85.2615L123.603 86.9684ZM123.603 415H121.842V416.761H123.603V415ZM238.286 415V416.761H240.047V415H238.286ZM238.286 -33.4807H240.047V-35.2419H238.286V-33.4807ZM139.62 -33.4807V-35.2419H139.315L139.027 -35.1389L139.62 -33.4807ZM16.1961 10.5684L43.7456 107.312L47.1333 106.347L19.5838 9.60367L16.1961 10.5684ZM45.8732 108.537L124.037 88.6754L123.17 85.2615L45.0057 105.123L45.8732 108.537ZM121.842 86.9684V415H125.364V86.9684H121.842ZM123.603 416.761H238.286V413.239H123.603V416.761ZM240.047 415V-33.4807H236.525V415H240.047ZM238.286 -35.2419H139.62V-31.7195H238.286V-35.2419ZM139.027 -35.1389L17.2965 8.42783L18.4834 11.7442L140.214 -31.8225L139.027 -35.1389Z',
  },
  Number2: {
    viewBox: '0 0 323 436',
    path: 'M338.077 416V417.761H339.839V416H338.077ZM338.077 305.161H339.839V303.4H338.077V305.161ZM197.767 305.161L196.496 303.942L193.636 306.922H197.767V305.161ZM258.632 241.733L257.362 240.513L257.362 240.514L258.632 241.733ZM284.26 -3.64978L283.118 -2.30855L283.134 -2.29555L283.149 -2.28288L284.26 -3.64978ZM17.0934 61.0596L15.4746 60.3658L14.8497 61.8238L16.2347 62.5973L17.0934 61.0596ZM115.759 116.159L114.9 117.696L116.608 118.65L117.378 116.852L115.759 116.159ZM172.78 175.742L174.059 176.953L174.067 176.945L174.075 176.936L172.78 175.742ZM24.1409 332.711L22.8621 331.5L22.3798 332.009V332.711H24.1409ZM24.1409 416H22.3798V417.761H24.1409V416ZM339.839 416V305.161H336.316V416H339.839ZM338.077 303.4H197.767V306.922H338.077V303.4ZM199.038 306.381L259.903 242.953L257.362 240.514L196.496 303.942L199.038 306.381ZM259.902 242.953C306.781 194.148 332.15 152.722 332.15 100.141H328.628C328.628 151.352 304.024 191.934 257.362 240.513L259.902 242.953ZM332.15 100.141C332.15 55.4732 316.586 20.346 285.37 -5.01666L283.149 -2.28288C313.439 22.328 328.628 56.3949 328.628 100.141H332.15ZM285.401 -4.991C254.942 -30.9136 218.008 -43.8522 174.702 -43.8522V-40.3298C217.249 -40.3298 253.353 -27.6409 283.118 -2.30855L285.401 -4.991ZM174.702 -43.8522C105.487 -43.8522 44.5993 -7.59192 15.4746 60.3658L18.7122 61.7534C47.2492 -4.83313 106.811 -40.3298 174.702 -40.3298V-43.8522ZM16.2347 62.5973L114.9 117.696L116.618 114.621L17.9521 59.5219L16.2347 62.5973ZM117.378 116.852C124.022 101.349 132.351 89.9331 142.141 82.3965C151.908 74.877 163.219 71.1497 175.984 71.1497V67.6273C162.481 67.6273 150.406 71.5883 139.992 79.6054C129.6 87.6055 120.951 99.5744 114.14 115.465L117.378 116.852ZM175.984 71.1497C188.133 71.1497 197.591 74.7268 204 80.8344C210.401 86.9343 213.945 95.7327 213.945 106.548H217.467C217.467 94.94 213.644 85.1585 206.43 78.2844C199.225 71.4181 188.822 67.6273 175.984 67.6273V71.1497ZM213.945 106.548C213.945 116.041 210.902 124.84 204.058 135.381C197.183 145.971 186.55 158.202 171.485 174.549L174.075 176.936C189.123 160.608 199.953 148.173 207.013 137.299C214.104 126.377 217.467 116.917 217.467 106.548H213.945ZM171.501 174.532L22.8621 331.5L25.4198 333.922L174.059 176.953L171.501 174.532ZM22.3798 332.711V416H25.9021V332.711H22.3798ZM24.1409 417.761H338.077V414.239H24.1409V417.761Z',
  },
  SlimArrowDown: {
    viewBox: '0 0 15 30',
    path: 'M6.81584 29.3048C7.20637 29.6953 7.83953 29.6953 8.23006 29.3048L14.594 22.9408C14.9845 22.5503 14.9845 21.9171 14.594 21.5266C14.2035 21.1361 13.5703 21.1361 13.1798 21.5266L7.52295 27.1834L1.8661 21.5266C1.47557 21.1361 0.842407 21.1361 0.451882 21.5266C0.061358 21.9171 0.061358 22.5503 0.451882 22.9408L6.81584 29.3048ZM6.52295 0.597656L6.52295 28.5977L8.52295 28.5977L8.52295 0.597656L6.52295 0.597656Z',
  },
  SlimArrowUp: {
    viewBox: '0 0 16 30',
    path: 'M8.94542 0.456955C8.5549 0.0664311 7.92173 0.066431 7.53121 0.456955L1.16724 6.82092C0.776721 7.21144 0.776721 7.84461 1.16724 8.23513C1.55777 8.62565 2.19093 8.62565 2.58146 8.23513L8.23831 2.57828L13.8952 8.23513C14.2857 8.62565 14.9189 8.62565 15.3094 8.23513C15.6999 7.84461 15.6999 7.21144 15.3094 6.82092L8.94542 0.456955ZM9.23831 29.1641L9.23831 1.16406L7.23831 1.16406L7.23831 29.1641L9.23831 29.1641Z',
  },
  EyeIcon: {
    viewBox: '0 0 32 32',
    path: [
      'M19.9961 15.9961C19.9961 17.057 19.5747 18.0744 18.8245 18.8245C18.0744 19.5747 17.057 19.9961 15.9961 19.9961C14.9352 19.9961 13.9178 19.5747 13.1677 18.8245C12.4175 18.0744 11.9961 17.057 11.9961 15.9961C11.9961 14.9352 12.4175 13.9178 13.1677 13.1677C13.9178 12.4175 14.9352 11.9961 15.9961 11.9961C17.057 11.9961 18.0744 12.4175 18.8245 13.1677C19.5747 13.9178 19.9961 14.9352 19.9961 15.9961Z',
      'M29.1887 15.4014C26.3113 9.6454 21.202 6.66406 15.9967 6.66406C10.7927 6.66406 5.68202 9.6454 2.80468 15.4014C2.71221 15.5865 2.66406 15.7905 2.66406 15.9974C2.66406 16.2043 2.71221 16.4083 2.80468 16.5934C5.68202 22.3494 10.7913 25.3307 15.9967 25.3307C21.2007 25.3307 26.3113 22.3494 29.1887 16.5934C29.2812 16.4083 29.3293 16.2043 29.3293 15.9974C29.3293 15.7905 29.2812 15.5865 29.1887 15.4014ZM15.9967 22.6641C12.038 22.6641 7.99402 20.5041 5.50335 15.9974C7.99402 11.4907 12.0367 9.33073 15.9967 9.33073C19.9553 9.33073 23.9993 11.4907 26.49 15.9974C23.9993 20.5041 19.9553 22.6641 15.9967 22.6641Z',
    ],
  },
};

export default Icon;

// Zoom button icons
type IProps = {
  width: number;
  height: number;
  color?: string;
};

export const ZoomPlus = ({ width, height, color = 'black' }: IProps) => (
  <svg
    width={width}
    height={height}
    viewBox='0 0 18 18'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <line
      x1='0.304688'
      y1='8.97266'
      x2='17.696'
      y2='8.97266'
      stroke={color}
      strokeWidth='1.75'
    />
    <line
      x1='9.00586'
      y1='0.292969'
      x2='9.00586'
      y2='17.6675'
      stroke={color}
      strokeWidth='1.75'
    />
  </svg>
);

export const ZoomMinus = ({ width, height, color = 'black' }: IProps) => (
  <svg
    width={width}
    height={height}
    viewBox='0 0 18 2'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <line
      x1='0.304688'
      y1='1.01367'
      x2='17.696'
      y2='1.01367'
      stroke={color}
      strokeWidth='1.75'
    />
  </svg>
);

export const ChevronRightThinner = ({
  width,
  height,
  color = 'black',
}: IProps) => (
  <svg
    width={width}
    height={height}
    viewBox='0 0 7 11'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M1.25977 9.80469L5.98023 5.08422L1.25977 0.363752'
      stroke={color}
      strokeWidth='1.00136'
    />
  </svg>
);
